import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { PdfViewer } from '@ipsoftsrl/pdf-viewer';
import { Button, LoadingSpinner, Modal } from '../../components/ui';
import { intranetApi as api } from '../../services/intranet-api';

interface Folder {
  folder_id: string;
  name: string;
  full_path: string;
  created_at: string;
}

interface FileItem {
  file_id: string;
  file_name: string;
  file_key: string;
  file_size_bytes: number | null;
  content_type: string;
  uploaded_at: string;
  uploaded_by_name: string;
}

interface FilesResponse {
  data: {
    folders: Folder[];
    files: FileItem[];
    currentPath: string;
    userRole: 'viewer' | 'editor';
    areaName: string;
  };
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

interface DownloadResponse {
  data: {
    downloadUrl: string;
    fileName: string;
    contentType: string;
    expiresInSeconds: number;
  };
}

interface UploadResponse {
  data: {
    fileId: string;
    uploadUrl: string;
    fileKey: string;
    expiresInSeconds: number;
  };
}

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.doc', '.xlsx', '.xls'];
const CONTENT_TYPE_MAP: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.doc': 'application/msword',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
};

function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot >= 0 ? fileName.substring(lastDot).toLowerCase() : '';
}

function getFileIcon(contentType: string): string {
  if (contentType === 'application/pdf') return '📄';
  if (contentType.includes('word')) return '📝';
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊';
  return '📎';
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Portale Intranet — file browser per una singola area.
 * Navigazione cartelle, visualizzazione PDF, download Word/Excel.
 */
export function IntranetFilesPage() {
  const { areaId } = useParams<{ areaId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentPath = searchParams.get('path') || '';

  const [folders, setFolders] = useState<Folder[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [userRole, setUserRole] = useState<'viewer' | 'editor'>('viewer');
  const [areaName, setAreaName] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // PDF viewer state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfFileName, setPdfFileName] = useState('');

  // Upload state
  const [uploading, setUploading] = useState(false);

  // New folder state
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  const loadFiles = useCallback(async () => {
    if (!areaId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentPath) params.set('path', currentPath);
      params.set('page', String(page));
      params.set('pageSize', '50');

      const response = await api.get<FilesResponse>(`/areas/${areaId}/files?${params.toString()}`);
      setFolders(response.data.data.folders);
      setFiles(response.data.data.files);
      setUserRole(response.data.data.userRole);
      setAreaName(response.data.data.areaName);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err) {
      console.error('Failed to load files:', err);
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [areaId, currentPath, page]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const navigateToFolder = (folderPath: string) => {
    setPage(1);
    setSearchParams({ path: folderPath });
  };

  const navigateUp = () => {
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    const parentPath = parts.join('/');
    setPage(1);
    if (parentPath) {
      setSearchParams({ path: parentPath });
    } else {
      setSearchParams({});
    }
  };

  const handleFileClick = async (file: FileItem) => {
    try {
      const response = await api.get<DownloadResponse>(
        `/areas/${areaId}/files/${file.file_id}/download`,
      );
      const { downloadUrl, contentType } = response.data.data;

      if (contentType === 'application/pdf') {
        // Open PDF viewer
        setPdfUrl(downloadUrl);
        setPdfFileName(file.file_name);
      } else {
        // Word/Excel: direct download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = file.file_name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = getFileExtension(file.name);
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      alert('Formato non supportato. Sono ammessi solo: PDF, Word (.docx/.doc) e Excel (.xlsx/.xls)');
      return;
    }

    const contentType = CONTENT_TYPE_MAP[ext] || 'application/octet-stream';

    setUploading(true);
    try {
      // 1. Get presigned upload URL
      const response = await api.post<UploadResponse>(`/areas/${areaId}/files`, {
        fileName: file.name,
        contentType,
        fileSizeBytes: file.size,
        folderPath: currentPath || undefined,
      });

      // 2. Upload file to S3
      await fetch(response.data.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });

      // 3. Reload file list
      await loadFiles();
    } catch (err) {
      console.error('Upload failed', err);
      alert('Errore durante il caricamento del file.');
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleDelete = async (file: FileItem) => {
    if (!confirm(`Eliminare "${file.file_name}"?`)) return;

    try {
      await api.delete(`/areas/${areaId}/files/${file.file_id}`);
      await loadFiles();
    } catch (err) {
      console.error('Delete failed', err);
      alert('Errore durante l\'eliminazione del file.');
    }
  };

  const handleDeleteFolder = async (folder: Folder) => {
    if (!confirm(`Eliminare la cartella "${folder.name}" e tutto il suo contenuto?`)) return;

    try {
      await api.delete(`/areas/${areaId}/folders/${folder.folder_id}`);
      await loadFiles();
    } catch (err) {
      console.error('Delete folder failed', err);
      alert('Errore durante l\'eliminazione della cartella.');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    setCreatingFolder(true);
    try {
      await api.post(`/areas/${areaId}/folders`, {
        name: newFolderName.trim(),
        parentPath: currentPath || undefined,
      });
      setNewFolderName('');
      setShowNewFolder(false);
      await loadFiles();
    } catch (err) {
      console.error('Create folder failed', err);
      alert('Errore durante la creazione della cartella.');
    } finally {
      setCreatingFolder(false);
    }
  };

  // Breadcrumb: always show area name, then subfolder path
  const pathParts = currentPath ? currentPath.split('/').filter(Boolean) : [];
  const breadcrumbs = [
    { label: areaName || '...', path: '' },
    ...pathParts.map((part, idx) => ({
      label: part,
      path: pathParts.slice(0, idx + 1).join('/'),
    })),
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="intranet-page">
      {/* PDF Viewer Modal */}
      {pdfUrl && (
        <Modal isOpen={!!pdfUrl} onClose={() => setPdfUrl(null)} title={pdfFileName} resizable>
          <PdfViewer url={pdfUrl} fileName={pdfFileName} />
        </Modal>
      )}

      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Button variant="ghost" size="sm" onClick={() => navigate('/portals/intranet')}>
            ← Aree
          </Button>
          <h2>{areaName || 'File'}</h2>
        </div>
        {userRole === 'editor' && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button variant="secondary" size="sm" onClick={() => setShowNewFolder(true)}>
              📁 Nuova Cartella
            </Button>
            <label className="upload-button">
              <span className="btn btn--primary btn--sm" style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
                {uploading ? '⏳ Caricamento...' : '📤 Carica File'}
              </span>
              <input
                type="file"
                accept=".pdf,.docx,.doc,.xlsx,.xls"
                onChange={handleUpload}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </label>
          </div>
        )}
      </div>

      {/* Breadcrumb */}
      <nav className="breadcrumb" aria-label="Percorso cartella">
        {breadcrumbs.map((crumb, idx) => (
          <span key={idx}>
            {idx > 0 && <span className="breadcrumb-separator"> / </span>}
            {idx === breadcrumbs.length - 1 ? (
              <span className="breadcrumb-current">{crumb.label}</span>
            ) : (
              <button
                className="breadcrumb-link"
                onClick={() => navigateToFolder(crumb.path)}
              >
                {crumb.label}
              </button>
            )}
          </span>
        ))}
      </nav>

      {/* New folder dialog */}
      {showNewFolder && (
        <div className="new-folder-bar">
          <input
            type="text"
            placeholder="Nome cartella..."
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            autoFocus
            maxLength={100}
          />
          <Button variant="primary" size="sm" onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()}>
            Crea
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}>
            Annulla
          </Button>
        </div>
      )}

      {/* Content */}
      <div className="intranet-file-list">
        {/* Back button */}
        {currentPath && (
          <div className="file-row file-row--folder" onClick={navigateUp} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigateUp()}>
            <span className="file-icon">⬆️</span>
            <span className="file-name">..</span>
            <span className="file-meta"></span>
          </div>
        )}

        {/* Folders */}
        {folders.map((folder) => (
          <div
            key={folder.folder_id}
            className="file-row file-row--folder"
          >
            <span className="file-icon" onClick={() => navigateToFolder(folder.full_path)} style={{ cursor: 'pointer' }}>📁</span>
            <span
              className="file-name"
              onClick={() => navigateToFolder(folder.full_path)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigateToFolder(folder.full_path)}
              style={{ cursor: 'pointer' }}
            >
              {folder.name}
            </span>
            <span className="file-meta">
              {new Date(folder.created_at).toLocaleDateString('it-IT')}
            </span>
            {userRole === 'editor' && (
              <div className="file-actions">
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }} title="Elimina cartella">
                  🗑️
                </Button>
              </div>
            )}
          </div>
        ))}

        {/* Files */}
        {files.map((file) => (
          <div key={file.file_id} className="file-row">
            <span className="file-icon">{getFileIcon(file.content_type)}</span>
            <span
              className="file-name file-name--clickable"
              onClick={() => handleFileClick(file)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleFileClick(file)}
            >
              {file.file_name}
            </span>
            <span className="file-meta">
              {formatFileSize(file.file_size_bytes)} · {new Date(file.uploaded_at).toLocaleDateString('it-IT')} · {file.uploaded_by_name}
            </span>
            <div className="file-actions">
              <Button variant="ghost" size="sm" onClick={() => handleFileClick(file)} title="Apri/Scarica">
                📥
              </Button>
              {userRole === 'editor' && (
                <Button variant="ghost" size="sm" onClick={() => handleDelete(file)} title="Elimina">
                  🗑️
                </Button>
              )}
            </div>
          </div>
        ))}

        {/* Empty state */}
        {folders.length === 0 && files.length === 0 && (
          <div className="empty-state">
            <p>Questa cartella è vuota.</p>
            {userRole === 'editor' && <p className="text-muted">Carica un file o crea una sottocartella.</p>}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination" role="navigation" aria-label="Paginazione file">
          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            ← Precedente
          </Button>
          <span className="pagination-info">Pagina {page} di {totalPages}</span>
          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            Successiva →
          </Button>
        </div>
      )}
    </div>
  );
}
